import React from "react";
import type { RetryBounds } from "../../../../utils/requestRegionalFloorplan";
/** Account for SVG/object-contain letterboxing, not just its DOM rectangle. */
export function sourcePoint(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  width: number,
  height: number
) {
  const scale = Math.min(rect.width / width, rect.height / height);
  if (!Number.isFinite(scale) || scale <= 0) return { x: 0, y: 0 };
  return {
    x: Math.max(
      0,
      Math.min(
        width,
        (clientX - rect.left - (rect.width - width * scale) / 2) / scale
      )
    ),
    y: Math.max(
      0,
      Math.min(
        height,
        (clientY - rect.top - (rect.height - height * scale) / 2) / scale
      )
    )
  };
}
export function ImportRetryBox({
  width,
  height,
  bounds,
  drawing,
  onChange,
  onCancel
}: {
  width: number;
  height: number;
  bounds?: RetryBounds;
  drawing: boolean;
  onChange: (bounds: RetryBounds) => void;
  onCancel: () => void;
}) {
  const [anchor, setAnchor] = React.useState<{ x: number; y: number }>();
  const [draft, setDraft] = React.useState<RetryBounds>();
  const makeBox = (x: number, y: number) => ({
    x: Math.floor(Math.min(anchor!.x, x)),
    y: Math.floor(Math.min(anchor!.y, y)),
    width: Math.round(Math.abs(x - anchor!.x)),
    height: Math.round(Math.abs(y - anchor!.y))
  });
  const shown = draft || bounds;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={`absolute inset-0 h-full w-full ${drawing ? "cursor-crosshair" : "pointer-events-none"}`}
      style={{ touchAction: "none" }}
      aria-label="Draw a retry box"
      tabIndex={drawing ? 0 : undefined}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          setAnchor(undefined);
          setDraft(undefined);
          onCancel();
        }
      }}
      onPointerDown={(e) => {
        if (!drawing || e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        setAnchor(
          sourcePoint(
            e.clientX,
            e.clientY,
            e.currentTarget.getBoundingClientRect(),
            width,
            height
          )
        );
        setDraft(undefined);
      }}
      onPointerMove={(e) => {
        if (!anchor || !drawing) return;
        const p = sourcePoint(
          e.clientX,
          e.clientY,
          e.currentTarget.getBoundingClientRect(),
          width,
          height
        );
        setDraft(makeBox(p.x, p.y));
      }}
      onPointerUp={(e) => {
        if (!anchor) return;
        const p = sourcePoint(
            e.clientX,
            e.clientY,
            e.currentTarget.getBoundingClientRect(),
            width,
            height
          ),
          box = makeBox(p.x, p.y);
        setAnchor(undefined);
        setDraft(undefined);
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        if (box.width >= 16 && box.height >= 16) onChange(box);
      }}
      onPointerCancel={() => {
        setAnchor(undefined);
        setDraft(undefined);
      }}
    >
      {shown && (
        <rect
          x={shown.x}
          y={shown.y}
          width={shown.width}
          height={shown.height}
          fill="#2563eb"
          fillOpacity={0.15}
          stroke="#2563eb"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}
